import { ArrowDown, ArrowUp, MoreVertical } from 'lucide-react'
import {
  Badge, Button, Menu, MenuContent, MenuItem, MenuTrigger,
} from '@bb/ui'
import { de, formatDate, formatRelative } from '../../i18n'
import { USER_COLUMNS, type SortDirection, type UserSortField } from './accountsTable'
import type { AccountListItem } from './types'

const GRID_TEMPLATE = 'minmax(9rem,1.4fr) minmax(10rem,1.6fr) minmax(8rem,1.3fr) 6rem 6.5rem 8rem 6rem 2.5rem'

export interface UsersTableProps {
  accounts: AccountListItem[]
  sort: UserSortField
  dir: SortDirection
  onSort: (field: UserSortField) => void
  onSelect: (account: AccountListItem) => void
  onViewDetails: (account: AccountListItem) => void
  onDelete: (account: AccountListItem) => void
}

/** §6's table, in the transactions view's own idiom — a CSS grid with a
    clickable, sortable header — but without that view's virtualization or
    column-fitting machinery: one row per account on one installation is a
    small, unpaged list (service.ts), so a plain grid is enough. */
export function UsersTable({ accounts, sort, dir, onSort, onSelect, onViewDetails, onDelete }: UsersTableProps) {
  return (
    <div className="min-w-0 overflow-x-auto rounded-[var(--radius-panel)] border border-rule">
      <div style={{ minWidth: '52rem' }}>
        <div
          role="row"
          className="grid items-center gap-3 border-b border-rule-strong bg-surface px-4 py-2 text-xs font-medium text-ink-muted"
          style={{ gridTemplateColumns: GRID_TEMPLATE }}
        >
          {USER_COLUMNS.map((column) => (
            <button
              key={column.field}
              type="button"
              onClick={() => onSort(column.field)}
              className="flex items-center gap-1 text-left hover:text-ink"
            >
              {column.label}
              {sort === column.field ? (
                dir === 'asc' ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />
              ) : null}
            </button>
          ))}
          <span aria-hidden />
        </div>

        <div className="divide-y divide-rule">
          {accounts.map((account, index) => (
            <div
              key={account.id}
              role="row"
              tabIndex={0}
              onClick={() => onSelect(account)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onSelect(account)
                }
              }}
              className={`grid cursor-default items-center gap-3 px-4 py-2 text-sm hover:bg-sunken ${index % 2 === 1 ? 'bg-band' : ''}`}
              style={{ gridTemplateColumns: GRID_TEMPLATE }}
            >
              <span className="min-w-0 truncate text-ink">{account.firstName} {account.lastName}</span>
              <span className="min-w-0 truncate text-ink-muted">{account.email}</span>
              <span className="min-w-0 truncate text-ink-muted">{account.companyName}</span>
              <span className="text-ink-muted">
                {account.role === 'owner' ? de.users.role.owner : de.users.role.member}
              </span>
              <span className="tabular text-ink-muted">{formatDate(account.createdAt)}</span>
              <span className="min-w-0 truncate text-ink-muted">
                {account.lastActiveAt ? formatRelative(account.lastActiveAt) : de.common.never}
              </span>
              <span>
                {account.isAdmin ? <Badge tone="accent">{de.users.yes}</Badge> : <span className="text-ink-faint">{de.users.no}</span>}
              </span>

              <span onClick={(event) => event.stopPropagation()}>
                <Menu>
                  <MenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label={de.users.rowMenu.open}>
                      <MoreVertical className="size-4" />
                    </Button>
                  </MenuTrigger>
                  <MenuContent>
                    <MenuItem onSelect={() => onViewDetails(account)}>{de.users.rowMenu.viewDetails}</MenuItem>
                    <MenuItem danger onSelect={() => onDelete(account)}>{de.users.rowMenu.deleteAccount}</MenuItem>
                  </MenuContent>
                </Menu>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
